import { EquipmentDropdownInput } from "#batteries-included-components/Dropdowns/EquipmentDropdownInput";
import { FacilityDropdownInput } from "#batteries-included-components/Dropdowns/FacilityDropdownInput";
import { FlowDropdownInput } from "#batteries-included-components/Dropdowns/FlowDropdownInput";
import { RecordValueConfigurationContext } from "#batteries-included-components/Layouts/RecordConfiguration/RecordConfigurationContext";
import { useParams } from "#routers/hooks";
import { FormCategoryDropdownInput } from "#src/batteries-included-components/Dropdowns/FormCategoryDropdownInput";
import { FormSchemaDropdownInput } from "#src/batteries-included-components/Forms/FormSubmissionForm/FormSubmissionSections/FormSubmissionField/QuestionField/LookupField/FormSchemaDropdownInput";
import { FilterArea } from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { getDefaultDateRange } from "#utils/recordUtils";
import {
  Accordion,
  DateSelectorInput,
  DropdownInput,
} from "@validereinc/common-components";
import { AssetType, AssetTypeSchema } from "@validereinc/domain";
import { DateTimeValueSchemas } from "@validereinc/utilities";
import React, { useContext, useMemo } from "react";
import { z } from "zod";

export const FormConfigurationFilterAreaSchema = z
  .object({
    assetType: AssetTypeSchema,
    formCategory: z.string(),
    formSchema: z.string(),
    dateRange: DateTimeValueSchemas.dateTimeRange,
  })
  .partial();
export type FormConfigurationFilterAreaType = z.infer<
  typeof FormConfigurationFilterAreaSchema
>;

export const FormConfigurationFilterPanel = ({
  storageKey,
}: {
  storageKey: string;
}) => {
  const { localize } = useLocalization();
  const { record } = useContext(RecordValueConfigurationContext) ?? {};
  const [storedFilters] = useStickyState<FormConfigurationFilterAreaType>(
    {},
    storageKey
  );
  const params = useParams<{ measurementType: string }>();

  const defaultDateRange = useMemo(
    () => getDefaultDateRange(record, params.measurementType),
    [record, params.measurementType]
  );

  const { assetType, formCategory } = storedFilters ?? {};

  return (
    <FilterArea.Root
      storageKey={storageKey}
      defaultValues={() => {
        const actualFormCategory = record?.values?.find(
          ({ measurement_type }) => measurement_type === params.measurementType
        )?.configuration?.form_category_id;

        return {
          formCategory: actualFormCategory,
          formSchema: storedFilters?.formSchema,
          ...(record?.asset_type === AssetType.FACILITY
            ? {
                facility: record?.asset_id,
                assetType: record.asset_type,
              }
            : {}),
          ...(record?.asset_type === AssetType.EQUIPMENT
            ? {
                equipment: record?.asset_id,
                assetType: record.asset_type,
              }
            : {}),
          ...(record?.asset_type === AssetType.FLOW
            ? {
                flow: record?.asset_id,
                assetType: record.asset_type,
              }
            : {}),
          dateRange: defaultDateRange,
        };
      }}
      applyDefaultValues
    >
      <FilterArea.Container aria-label="Form Configuration Filters">
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div style={{ display: "flex", gap: 8 }}>
              <DropdownInput
                label="Asset Type"
                name="assetType"
                options={[
                  {
                    name: localize("Facility"),
                    id: "facility",
                  },
                  { name: localize("Equipment"), id: "equipment" },
                  { name: localize("Flow"), id: "flow" },
                ]}
                labelKey="name"
                valueKey="id"
                isSearchable
                isInline
                isOptionalTextShown={false}
                isLabelShown={false}
                placeholder="Select Asset Type"
                onChange={(val) => handleOnChange(val, "assetType")}
              />
              <FormCategoryDropdownInput
                filter={{
                  measurement_type: params.measurementType,
                  archived: false,
                }}
                isRequired
                label="Form Category"
                placeholder="Search Form Categories"
                name="formCategory"
                isFluid={false}
                isInline
                isLabelShown={false}
                onChange={(val) => handleOnChange(val, "formCategory")}
              />
            </div>
          )}
        </FilterArea.Content>
        <FilterDrawer.Root>
          <FilterDrawer.Trigger style={{ marginLeft: 8 }} />
          <FilterDrawer.Content>
            <FormSchemaDropdownInput
              filter={{ form_category_id: formCategory, status: "active" }}
              isRequired
              label="Form Template"
              isDisabled={!formCategory}
              placeholder="Search Form Template"
              name="formSchema"
              isFluid={false}
            />
            <DateSelectorInput
              name="dateRange"
              variant={"time"}
              label="Submission Date"
              isRequired
              isRange
            />
            <Accordion defaultActiveKeys={["asset"]}>
              <Accordion.AccordionPanel
                title="Asset"
                dataKey="asset"
              >
                {assetType === AssetType.FACILITY ? (
                  <FacilityDropdownInput
                    key="facility"
                    name="facility"
                    isMulti={false}
                    isFluid
                    isRequired
                  />
                ) : null}
                {assetType === AssetType.EQUIPMENT ? (
                  <EquipmentDropdownInput
                    key="equipment"
                    name="equipment"
                    isMulti={false}
                    isFluid
                    isRequired
                  />
                ) : null}
                {assetType === AssetType.FLOW ? (
                  <FlowDropdownInput
                    key="flow"
                    name="flow"
                    isMulti={false}
                    isFluid
                    isRequired
                  />
                ) : null}
              </Accordion.AccordionPanel>
            </Accordion>
          </FilterDrawer.Content>
        </FilterDrawer.Root>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
